import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/dashboard.css';
import Header from '../component/header'; // Added Header import

const BASE_URL = process.env.REACT_APP_BASE_URL;

const DateWiseSurvey = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();

    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            // Page titles
            title: "Date Wise Survey",
            subtitle: "Overview of survey progress by specific dates.",
            
            // Month Picker
            selectMonthYear: "Select Month & Year",
            cancel: "Cancel",
            apply: "Apply",
            
            // Messages
            loading: "Loading Data...",
            noData: "No survey data available for this month.",
            
            // Labels
            survey: "Survey :",
            
            // Month names (already in English, but keeping for consistency)
            months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
        },
        
        hi: {
            // Page titles
            title: "तिथि वार सर्वेक्षण",
            subtitle: "विशिष्ट तिथियों द्वारा सर्वेक्षण प्रगति का अवलोकन।",
            
            // Month Picker
            selectMonthYear: "महीना और वर्ष चुनें",
            cancel: "रद्द करें",
            apply: "लागू करें",
            
            // Messages
            loading: "डेटा लोड हो रहा है...",
            noData: "इस महीने के लिए कोई सर्वेक्षण डेटा उपलब्ध नहीं है।",
            
            // Labels
            survey: "सर्वेक्षण :",
            
            // Month names
            months: ["जन", "फ़र", "मार", "अप्रै", "मई", "जून", "जुल", "अग", "सित", "अक्टू", "नव", "दिस"]
        },
        
        gu: {
            // Page titles
            title: "તારીખ વાઈઝ સર્વે",
            subtitle: "ચોક્કસ તારીખો દ્વારા સર્વે પ્રગતિની ઝાંખી.",
            
            // Month Picker
            selectMonthYear: "મહિનો અને વર્ષ પસંદ કરો",
            cancel: "રદ કરો",
            apply: "લાગુ કરો",
            
            // Messages
            loading: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "આ મહિના માટે કોઈ સર્વે ડેટા ઉપલબ્ધ નથી.",
            
            // Labels
            survey: "સર્વે :",
            
            // Month names
            months: ["જાન", "ફેબ", "માર્ચ", "એપ્રિલ", "મે", "જૂન", "જુલાઈ", "ઑગ", "સપ્ટે", "ઑક્ટો", "નવે", "ડિસે"]
        }
    };

    const txt = t[lang];

    // Initialize with Current Year and Month (Format: YYYY-MM)
    const today = new Date();
    const currentMonthStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

    const [selectedMonth, setSelectedMonth] = useState(currentMonthStr);
    const [dateData, setDateData] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- Custom Picker States ---
    const [showPicker, setShowPicker] = useState(false);
    const [pickerMonth, setPickerMonth] = useState(today.getMonth() + 1);
    const [pickerYear, setPickerYear] = useState(today.getFullYear());

    const monthNames = txt.months;
    const yearList = Array.from({ length: 20 }, (_, i) => 2020 + i); // 2020 to 2039

    useEffect(() => {
        if (appId && selectedMonth) {
            fetchData();
        }
    }, [appId, selectedMonth]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Convert "YYYY-MM" to "M-YYYY" as required by API
            const [year, month] = selectedMonth.split('-');
            const formattedMonthPayload = `${parseInt(month, 10)}-${year}`;

            const response = await axios.post(`${BASE_URL}/api/dis_date_wise_survey_dash`, {
                app_id: appId,
                month: formattedMonthPayload
            });

            if (response.data.success) {
                setDateData(response.data.data.result || []);
            }
        } catch (error) {
            console.error("Error fetching date wise survey:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- NEW: Navigation Handler ---
    const handleCardClick = (dateStr) => {
        // Change '/date_survey_details' to the actual route of your target page
        // We pass the specific date string in the state so the next page can fetch details
        navigate('/dis_date_wise_survey_voter', { state: { date: dateStr } });
    };

    // Open Picker and sync with current selected value
    const openPicker = () => {
        const [year, month] = selectedMonth.split('-');
        setPickerYear(parseInt(year, 10));
        setPickerMonth(parseInt(month, 10));
        setShowPicker(true);
    };

    // Apply the selected date from popup
    const handleApplyPicker = () => {
        const mm = String(pickerMonth).padStart(2, '0');
        setSelectedMonth(`${pickerYear}-${mm}`);
        setShowPicker(false);
    };

    // Formatting for display on the button
    const getDisplayDate = () => {
        const [year, month] = selectedMonth.split('-');
        return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
    };

    return (
        <div className="app-container dashboard-page" style={{ backgroundColor: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div className='dashboard-content container-fluid p-4'>
                
                {/* --- Header Component Integrated Here --- */}
                <Header 
                    title={txt.title}
                />

                {/* --- Custom Month Picker Trigger Row --- */}
                <div className="row fade-in-up mt-3" style={{ animationDelay: '0.1s' }}>
                    <div className="col-12">
                        <div className="d-flex justify-content-end align-items-center flex-wrap gap-3">
                            <div className="month-picker-container">
                                <div className="month-picker-wrapper" onClick={openPicker}>
                                    <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="calendar-icon">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="native-month-input">
                                        {getDisplayDate()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Content List --- */}
                <div className="container-fluid p-0 mt-4" style={{ flex: 1 }}>
                    {loading ? (
                        <div className="text-center p-5 text-muted">{txt.loading}</div>
                    ) : (
                        <div className="row g-4">
                            {dateData.length > 0 ? (
                                dateData.map((item, index) => (
                                    <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3 fade-in-up">
                                        {/* ADDED: onClick handler and cursor pointer */}
                                        <div 
                                            className="date-survey-card h-100" 
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleCardClick(item.date)}
                                        >
                                            <div className="d-card-date">{item.date}</div>
                                            <div className="d-card-survey-row">
                                                <span className="survey-label">{txt.survey}</span>
                                                <span className="survey-value">{item.total}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 text-center text-muted p-5 empty-state">
                                    <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="mb-3">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <p>{txt.noData}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>

            {/* --- CUSTOM POPUP PICKER MODAL --- */}
            {showPicker && (
                <div className="custom-picker-overlay" onClick={() => setShowPicker(false)}>
                    <div className="custom-picker-modal fade-in-up" onClick={(e) => e.stopPropagation()}>
                        <h3 className="picker-title">{txt.selectMonthYear}</h3>
                        
                        <div className="picker-columns">
                            {/* Months Scroll Column */}
                            <div className="picker-scroll-col">
                                {monthNames.map((m, i) => (
                                    <div 
                                        key={m} 
                                        className={`picker-item ${pickerMonth === i + 1 ? 'selected' : ''}`}
                                        onClick={() => setPickerMonth(i + 1)}
                                    >
                                        {m}
                                    </div>
                                ))}
                            </div>
                            
                            {/* Years Scroll Column */}
                            <div className="picker-scroll-col">
                                {yearList.map((y) => (
                                    <div 
                                        key={y} 
                                        className={`picker-item ${pickerYear === y ? 'selected' : ''}`}
                                        onClick={() => setPickerYear(y)}
                                    >
                                        {y}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="picker-actions">
                            <button className="p-btn p-cancel" onClick={() => setShowPicker(false)}>{txt.cancel}</button>
                            <button className="p-btn p-apply" onClick={handleApplyPicker}>{txt.apply}</button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                /* Enhanced Month Picker Trigger Styling */
                .month-picker-container {
                    display: flex;
                    justify-content: flex-end; /* Aligns to right */
                    align-items: center;
                }
                
                .month-picker-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: #ffffff;
                    padding: 10px 24px;
                    border-radius: 50px; /* Pill shape */
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                
                .month-picker-wrapper:hover {
                    border-color: #3b82f6;
                    box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.15);
                    transform: translateY(-2px);
                }
                
                .calendar-icon {
                    color: #64748b;
                    transition: color 0.3s;
                }
                
                .month-picker-wrapper:hover .calendar-icon {
                    color: #3b82f6; 
                }
                
                .native-month-input {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1e293b;
                    user-select: none;
                }

                /* --- CUSTOM POPUP PICKER STYLES --- */
                .custom-picker-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(2px);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 9999;
                }
                .custom-picker-modal {
                    background: #ffffff;
                    width: 90%;
                    max-width: 320px;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                }
                .picker-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1e293b;
                    text-align: center;
                    margin-top: 0;
                    margin-bottom: 20px;
                }
                .picker-columns {
                    display: flex;
                    gap: 15px;
                    height: 200px;
                    border-top: 1px solid #f1f5f9;
                    border-bottom: 1px solid #f1f5f9;
                    padding: 10px 0;
                }
                .picker-scroll-col {
                    flex: 1;
                    overflow-y: auto;
                    scroll-behavior: smooth;
                    padding-right: 5px;
                }
                /* Custom scrollbar for popup */
                .picker-scroll-col::-webkit-scrollbar { width: 4px; }
                .picker-scroll-col::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 4px; }
                
                .picker-item {
                    padding: 10px;
                    text-align: center;
                    font-size: 15px;
                    font-weight: 500;
                    color: #64748b;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .picker-item:hover {
                    background-color: #f8fafc;
                }
                .picker-item.selected {
                    background-color: #eff6ff;
                    color: #2563eb;
                    font-weight: 700;
                }
                .picker-actions {
                    display: flex;
                    justify-content: space-between;
                    gap: 15px;
                    margin-top: 20px;
                }
                .p-btn {
                    flex: 1;
                    padding: 10px;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    border: none;
                    transition: 0.2s;
                }
                .p-cancel {
                    background-color: #f1f5f9;
                    color: #475569;
                }
                .p-cancel:hover {
                    background-color: #e2e8f0;
                }
                .p-apply {
                    background-color: #3b82f6;
                    color: white;
                }
                .p-apply:hover {
                    background-color: #2563eb;
                }

                /* Enhanced Date Card Styles */
                .date-survey-card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    border-top: 4px solid #1e3a8a; /* Top accent border */
                    padding: 24px 20px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .date-survey-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
                }
                .d-card-date {
                    font-size: 14px;
                    font-weight: 700;
                    color: #64748b;
                    margin-bottom: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .d-card-survey-row {
                    display: flex;
                    align-items: baseline;
                    justify-content: center;
                    gap: 8px;
                }
                .survey-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #334155;
                }
                .survey-value {
                    font-size: 24px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: 0.5px;
                }

                /* Empty State */
                .empty-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    color: #94a3b8;
                }

                /* Footer Bar */
                .footer-bar {
                    background-color: #0f172a; /* Dark Footer */
                    padding: 15px 25px;
                    position: sticky;
                    bottom: 0;
                    display: flex;
                    justify-content: flex-start;
                    z-index: 50;
                    box-shadow: 0 -4px 10px rgba(0,0,0,0.1);
                }
                .footer-text {
                    font-size: 15px;
                    color: #0f172a;
                    background: white;
                    padding: 6px 20px;
                    border-radius: 6px;
                    font-weight: 600;
                }

                /* =========================================
                   RESPONSIVE MEDIA QUERIES (MOBILE FIXES)
                   ========================================= */
                @media (max-width: 768px) {
                    .page-header {
                        align-items: flex-start !important;
                        flex-direction: column;
                        gap: 16px;
                    }
                    .header-content {
                        margin-bottom: 8px;
                    }
                    .month-picker-container {
                        width: 100%;
                        justify-content: flex-start;
                    }
                    .month-picker-wrapper {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 480px) {
                    .date-survey-card {
                        padding: 16px 12px;
                    }
                    .d-card-date {
                        font-size: 13px;
                        margin-bottom: 8px;
                    }
                    .survey-label {
                        font-size: 13px;
                    }
                    .survey-value {
                        font-size: 20px;
                    }
                    .custom-picker-modal {
                        width: 95%;
                        padding: 20px 16px;
                    }
                    .native-month-input {
                        font-size: 15px;
                    }
                }
            `}</style>
        </div>
    );
};

export default DateWiseSurvey;